import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PortalCashTransactionsLedger,
  type CashTransactionFilterState,
  type CashTransactionListResponse,
} from '@kioskgaming/ui';
import type { SortDirection } from '@kioskgaming/ui/dist/data-table/sortState';
import useTableSort from '../../../hooks/useTableSort';
import { getTableSortColumns } from '../../../config/tableSortConfig';
import { usePermission } from '../../../hooks/useAuth';
import { apiClient } from '../../../services/api';
import { downloadBlob } from '../../../utils';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';
import { Card, CardContent } from '../../ui/Card';

const CashTransactionsLedgerPage: React.FC = () => {
  const canView = usePermission('cashSettlementManagement');
  const navigate = useNavigate();
  const [data, setData] = useState<CashTransactionListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [sortFetchTick, setSortFetchTick] = useState(0);

  const tableSort = useTableSort({
    columns: getTableSortColumns('cashTransactions'),
    onChange: () => setSortFetchTick((t) => t + 1),
  });

  const appliedSort = useMemo(
    () => ({
      sortBy: (data?.pagination?.sortBy ?? null) as string | null,
      sortOrder: (data?.pagination?.sortOrder ?? null) as SortDirection | null,
    }),
    [data?.pagination?.sortBy, data?.pagination?.sortOrder],
  );

  const headerSort = tableSort.sortBy
    ? {
        activeKey: appliedSort.sortBy ?? tableSort.sortBy,
        direction: appliedSort.sortOrder ?? tableSort.sortOrder,
      }
    : {
        activeKey: appliedSort.sortBy,
        direction: appliedSort.sortOrder,
      };

  const onFetch = useCallback(
    async ({
      filters,
      page,
      limit,
    }: {
      filters: CashTransactionFilterState;
      page: number;
      limit: number;
    }) => {
      if (!canView) return;
      setLoading(true);
      try {
        const res = await apiClient.getAdminCashTransactions({
          page,
          limit,
          flow: filters.flow || undefined,
          status: filters.status || undefined,
          payment_method: filters.paymentMethod || undefined,
          date_from: filters.dateFrom || undefined,
          date_to: filters.dateTo || undefined,
          search: filters.search.trim() || undefined,
          ...tableSort.sortParams,
        });
        setData(res.data);
      } catch (e: unknown) {
        setData(null);
        toast.error(e instanceof Error ? e.message : 'Could not load cash transactions');
      } finally {
        setLoading(false);
      }
    },
    [canView, tableSort.sortParams],
  );

  const handleExport = useCallback(async ({ filters }: { filters: CashTransactionFilterState }) => {
    setExporting(true);
    try {
      const { blob, filename } = await apiClient.exportAdminCashTransactions({
        flow: filters.flow || undefined,
        status: filters.status || undefined,
        payment_method: filters.paymentMethod || undefined,
        date_from: filters.dateFrom || undefined,
        date_to: filters.dateTo || undefined,
        search: filters.search.trim() || undefined,
      });
      downloadBlob(blob, filename);
      toast.success('Cash transactions exported');
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, []);

  if (!canView) {
    return <AccessDeniedPanel message="You don't have permission to view cash transactions." />;
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-0 sm:p-0">
        {data?.pagination?.sortWarning ? (
          <p className="mx-6 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {data.pagination.sortWarning}
          </p>
        ) : null}
        <PortalCashTransactionsLedger
          title="Cash Transactions"
          description="History of cash credit sales in the hierarchy. Sell credits from Super Agent detail."
          loading={loading}
          data={data}
          onFetch={onFetch}
          onExport={handleExport}
          exporting={exporting}
          onViewDetail={(row) =>
            navigate(
              `/cash-transactions/${row.id}?created_at=${encodeURIComponent(row.createdAt)}`,
            )
          }
          className="border-0 shadow-none rounded-none"
          showCreatedColumn
          sortable
          sortActiveKey={headerSort.activeKey}
          sortDirection={headerSort.direction}
          onSort={tableSort.toggleSort}
          sortFetchTick={sortFetchTick}
        />
      </CardContent>
    </Card>
  );
};

export default CashTransactionsLedgerPage;
